import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, useMotionValue, useTransform, animate, useReducedMotion } from "framer-motion";
import CSSReveal from "../components/CSSReveal";
import axios from "axios";
import {
  Globe,
  Users,
  Receipt,
  Sparkles,
  ShieldCheck,
  ArrowRight,
  Zap,
  MapPin,
  FileText,
  PenTool,
  FileSignature,
  Building2,
  Server,
  Lock,
  Award,
  TrendingUp,
  Layers,
  MessageSquare,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// ────────────────────────────────────────────────────────────
// Live-Zähler-Animation
// ────────────────────────────────────────────────────────────
const AnimatedCounter = ({ to, suffix = "", prefix = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const mv = useMotionValue(0);
  const rounded = useTransform(mv, (v) => `${prefix}${Math.round(v).toLocaleString("de-DE")}${suffix}`);
  // Direkter Fallback: wenn `to` numerisch ist, initialisiere die Anzeige direkt
  // mit dem finalen Wert. So wird nichts als 0 gerendert, falls IntersectionObserver
  // aus Timing-/Layout-Gründen nicht triggert (z.B. Chrome-iOS Scroll-Hijacking).
  const safeTarget = Number.isFinite(Number(to)) ? Number(to) : 0;
  const [display, setDisplay] = useState(`${prefix}${safeTarget.toLocaleString("de-DE")}${suffix}`);
  useEffect(() => {
    if (!inView) return;
    // Von 0 zum Ziel animieren
    mv.set(0);
    const controls = animate(mv, safeTarget, { duration: 1.6, ease: [0.16, 1, 0.3, 1] });
    const unsub = rounded.on("change", (v) => setDisplay(v));
    return () => { controls.stop(); unsub(); };
  }, [inView, safeTarget, mv, rounded]);
  return <span ref={ref} className="tabular-nums">{display}</span>;
};

// ────────────────────────────────────────────────────────────
// Netzwerk-Graph SVG in Hero (Nodes + pulsierende Verbindungen)
// ────────────────────────────────────────────────────────────
const NetworkGraph = () => {
  // Animationen nur laufen lassen, solange der Graph tatsächlich sichtbar ist –
  // ansonsten repaintet der Browser 18 SVG-Puls-Punkte im Loop auch dann noch,
  // wenn der Nutzer längst weit runtergescrollt hat. Das war die Ursache für
  // das ruckelige Scroll-Verhalten auf mobilen Geräten.
  const wrapperRef = useRef(null);
  const isVisible = useInView(wrapperRef, { margin: "0px 0px -20% 0px" });

  // 12 Nodes rund um Center – jeder ist ein Modul
  const nodes = [
    { id: "core", cx: 400, cy: 300, r: 26, label: "Platrion", color: "#3b82f6", center: true },
    { id: "web", cx: 220, cy: 160, r: 16, label: "Websites", color: "#60a5fa" },
    { id: "crm", cx: 580, cy: 160, r: 16, label: "CRM", color: "#a78bfa" },
    { id: "sign", cx: 640, cy: 300, r: 16, label: "eIDAS", color: "#f59e0b" },
    { id: "invoice", cx: 580, cy: 440, r: 16, label: "Rechnung", color: "#10b981" },
    { id: "content", cx: 400, cy: 500, r: 16, label: "Content", color: "#ec4899" },
    { id: "datev", cx: 220, cy: 440, r: 16, label: "DATEV", color: "#22d3ee" },
    { id: "seo", cx: 160, cy: 300, r: 16, label: "SEO", color: "#84cc16" },
    { id: "shop", cx: 300, cy: 90, r: 12, label: "Shop", color: "#f97316" },
    { id: "book", cx: 500, cy: 90, r: 12, label: "Booking", color: "#eab308" },
    { id: "chat", cx: 700, cy: 220, r: 12, label: "Chat", color: "#c084fc" },
    { id: "print", cx: 100, cy: 380, r: 12, label: "QR & Print", color: "#f472b6" },
  ];
  // Kanten: Center-to-Everything + einige Cross-Verbindungen
  const edges = [
    ["core","web"],["core","crm"],["core","sign"],["core","invoice"],
    ["core","content"],["core","datev"],["core","seo"],
    ["web","seo"],["seo","content"],["crm","sign"],["sign","invoice"],
    ["invoice","datev"],["shop","invoice"],["book","crm"],
    ["chat","crm"],["print","web"],["shop","web"],["book","web"],
  ];
  const nodeMap = Object.fromEntries(nodes.map(n => [n.id, n]));

  // Puls-Punkte nur auf jeder zweiten Kante rendern (9 statt 18) –
  // reicht visuell völlig aus und halbiert die Repaint-Last.
  const pulseEdgeIndices = edges.map((_, i) => i).filter((i) => i % 2 === 0);

  return (
    <div ref={wrapperRef} className="absolute inset-0 w-full h-full pointer-events-none">
      <svg
        viewBox="0 0 800 600"
        className="absolute inset-0 w-full h-full opacity-90"
        preserveAspectRatio="xMidYMid meet"
        data-testid="landscape-network-graph"
        aria-hidden="true"
        style={{ transform: "translateZ(0)", willChange: isVisible ? "transform" : "auto" }}
      >
        <defs>
          <radialGradient id="core-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#60a5fa" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#60a5fa" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="edge-gradient" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#60a5fa" stopOpacity="0" />
            <stop offset="50%" stopColor="#60a5fa" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#60a5fa" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Core-Halo */}
        <circle cx="400" cy="300" r="120" fill="url(#core-glow)" />

        {/* Statische Kanten (immer sichtbar, ohne Animation) */}
        {edges.map(([a,b], i) => {
          const A = nodeMap[a]; const B = nodeMap[b];
          return (
            <line key={`e-${i}`}
              x1={A.cx} y1={A.cy} x2={B.cx} y2={B.cy}
              stroke="rgba(148,163,184,0.15)" strokeWidth="1" />
          );
        })}

        {/* Puls-Punkte nur auf halber Kanten-Menge und NUR wenn im Viewport */}
        {isVisible && pulseEdgeIndices.map((i) => {
          const [a, b] = edges[i];
          const A = nodeMap[a]; const B = nodeMap[b];
          return (
            <motion.circle key={`p-${i}`}
              cx={A.cx} cy={A.cy} r={2.5}
              initial={{ cx: A.cx, cy: A.cy, opacity: 0 }}
              fill="#93c5fd"
              animate={{ cx: [A.cx, B.cx], cy: [A.cy, B.cy], opacity: [0, 1, 0] }}
              transition={{
                duration: 3 + (i % 4),
                repeat: Infinity,
                delay: (i * 0.35) % 3,
                ease: "linear",
              }}
            />
          );
        })}

        {/* Nodes */}
        {nodes.map((n, i) => (
          <motion.g
            key={n.id}
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.1 + i * 0.05, ease: [0.16, 1, 0.3, 1] }}
          >
            {n.center && isVisible && (
              <motion.circle
                cx={n.cx} cy={n.cy} r={n.r + 6}
                initial={{ r: n.r + 6, opacity: 0.5 }}
                fill="none" stroke={n.color} strokeWidth="1.5" strokeOpacity="0.35"
                animate={{ r: [n.r + 6, n.r + 18, n.r + 6], opacity: [0.5, 0, 0.5] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              />
            )}
            <circle cx={n.cx} cy={n.cy} r={n.r}
              fill={n.color} fillOpacity={n.center ? "0.9" : "0.75"}
              stroke={n.color} strokeWidth="1.5" strokeOpacity="0.9" />
            <text x={n.cx} y={n.cy + n.r + 14}
              textAnchor="middle"
              className={n.center ? "text-[13px] font-bold" : "text-[10px] font-medium"}
              fill="rgba(226,232,240,0.85)"
              style={{ fontFamily: "ui-sans-serif, system-ui" }}
            >
              {n.label}
            </text>
          </motion.g>
        ))}
      </svg>
    </div>
  );
};

// ────────────────────────────────────────────────────────────
// Section Wrapper mit Fade-In
// CSS-basierter Fade-In statt motion.section – auf iOS Safari registriert
// Framer-Motion sonst Pointer-Handler auf jedem Section-Element und
// „verschluckt" den Body-Scroll sobald der Finger kurz still liegt.
// ────────────────────────────────────────────────────────────
const Section = ({ children, className = "", ...props }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const reducedMotion = useReducedMotion();
  const visible = inView || reducedMotion;
  return (
    <section
      ref={ref}
      className={className}
      data-public-motion-owned=""
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translate3d(0,0,0)" : "translate3d(0,22px,0)",
        transition: reducedMotion ? "none" : "opacity 0.85s ease-out, transform 1.1s cubic-bezier(0.16,1,0.3,1)",
        touchAction: "pan-y",
      }}
      {...props}
    >
      {children}
    </section>
  );
};

// ────────────────────────────────────────────────────────────
// Die 5 Säulen
// ────────────────────────────────────────────────────────────
const PILLARS = [
  {
    icon: Globe,
    title: "Websites",
    subtitle: "Express 24h · Solo bis Enterprise",
    color: "from-blue-500/20 to-cyan-500/10",
    accent: "text-blue-300",
    border: "border-blue-500/30",
    modules: [
      "Privat 790 € / Solo & Klein 1.490 € / Solo Plus 2.490 €",
      "Starter 4.900 € · Professional 14.900 € · Enterprise 29.900 €",
      "Hosting in Deutschland · SSL · Cloudflare-Edge",
      "10 Live-Demos für Handwerk, Gastro, Praxen …",
      "9 Städte + 3 Regionen mit lokaler SEO (SEO-optimiert pro Standort)",
    ],
  },
  {
    icon: Users,
    title: "CRM & Pipeline",
    subtitle: "Vom Lead bis zum Bestandskunden",
    color: "from-purple-500/20 to-fuchsia-500/10",
    accent: "text-purple-300",
    border: "border-purple-500/30",
    modules: [
      "Kanban 7 Stufen: Neu → Kontaktiert → Angebot → Gewonnen",
      "Client-Constellation: alle Kunden auf einer Karte",
      "Firmenbesucher-Erkennung (Reverse-IP-Lookup)",
      "Manuelle Leads mit Paket, Hosting- und Domain-Ablauf",
      "Heatmaps + Traffic-Herkunft (inkl. AI-Bots)",
    ],
  },
  {
    icon: Receipt,
    title: "Buchhaltung",
    subtitle: "Rechnung · Vertrag · DATEV",
    color: "from-emerald-500/20 to-teal-500/10",
    accent: "text-emerald-300",
    border: "border-emerald-500/30",
    modules: [
      "Rechnungen mit § 14 UStG-konformer Nummerierung",
      "Werkverträge nach § 631 BGB + AVV nach Art. 28 DSGVO",
      "DATEV-Export für den Steuerberater",
      "Stripe + PayPal + SEPA-Erkennung",
      "10-Jahre-Aufbewahrung + Storno-Rechnung statt Löschen",
    ],
  },
  {
    icon: Sparkles,
    title: "Content & SEO",
    subtitle: "AI-generiert, redaktionell geprüft",
    color: "from-pink-500/20 to-rose-500/10",
    accent: "text-pink-300",
    border: "border-pink-500/30",
    modules: [
      "35+ Magazin-Artikel, alle rank-optimiert",
      "Content-Engine mit GPT-5 + Claude Sonnet 5",
      "Lokale SEO für Reutlingen, Tübingen, Stuttgart …",
      "llms.txt + IndexNow für sofortige AI-Sichtbarkeit",
      "Prerender + SSR für perfektes First-Contentful-Paint",
    ],
  },
  {
    icon: ShieldCheck,
    title: "Signatur & Legal",
    subtitle: "eIDAS Art. 25 · § 371a ZPO",
    color: "from-amber-500/20 to-orange-500/10",
    accent: "text-amber-300",
    border: "border-amber-500/30",
    modules: [
      "Digitale Signatur nach eIDAS Art. 25 (SES)",
      "SHA-256 Doc-Hash + FreeTSA-Zeitstempel (RFC 3161)",
      "Audit-Trail als forensische PDF-Anhangs-Seite",
      "Widerrufsverzicht für B2C nach § 356 BGB",
      "AVV-Zustimmung inline im Signatur-Flow",
    ],
  },
];

// ────────────────────────────────────────────────────────────
// Workflow-Schritte
// ────────────────────────────────────────────────────────────
const WORKFLOW = [
  { icon: MessageSquare, title: "Lead",       desc: "Kontaktformular, WhatsApp, Empfehlung",           color: "#60a5fa" },
  { icon: FileText,      title: "Angebot",    desc: "Auto-generiertes PDF mit dem passenden Paket",     color: "#a78bfa" },
  { icon: PenTool,       title: "Signatur",   desc: "eIDAS-konform in 2 Klicks vom Handy",              color: "#f59e0b" },
  { icon: FileSignature, title: "Vertrag",    desc: "Werkvertrag + AVV automatisch verknüpft",           color: "#fb923c" },
  { icon: Receipt,       title: "Rechnung",   desc: "Abschlags- + Endrechnung mit Stripe / SEPA",       color: "#10b981" },
  { icon: Building2,     title: "DATEV",      desc: "Monatlicher Export für den Steuerberater",         color: "#22d3ee" },
];

// ────────────────────────────────────────────────────────────
// Tech-Stack Grid
// ────────────────────────────────────────────────────────────
const TECH_STACK = [
  { icon: Server,       label: "Deutsche Server-Region",  hint: "Rechenzentrum in Deutschland · Auftragsverarbeiter transparent dokumentiert · DSGVO-Kernanforderungen erfüllt" },
  { icon: Lock,         label: "JWT + WebAuthn Passkeys", hint: "Passwortlose Anmeldung mit Face-ID / Fingerprint" },
  { icon: Award,        label: "eIDAS Art. 25",           hint: "Elektronische Signatur nach EU-Verordnung 910/2014" },
  { icon: TrendingUp,   label: "PageSpeed 100 / 100",     hint: "Lighthouse Desktop-Score über alle Seiten" },
  { icon: Layers,       label: "React 19 + FastAPI",      hint: "Modernster Stack – schnell, wartbar, zukunftssicher" },
  { icon: Sparkles,     label: "GPT-5 + Claude Sonnet 5", hint: "AI-Content-Engine für Blog, Landing-Pages, Descriptions" },
];


// ────────────────────────────────────────────────────────────
// Regionale SEO-Anchor-Blöcke (unique content pro Region – nicht
// dupliziert aus /webdesign-<region>-Seiten, sondern fokussiert
// auf den Ökosystem-Aspekt für regionale Suchen).
// ────────────────────────────────────────────────────────────
const REGIONS_SEO = [
  {
    slug: "neckar-alb",
    anchor: "region-neckar-alb",
    displayName: "Region Neckar-Alb",
    accent: "text-blue-300",
    ring: "border-blue-500/25",
    tint: "from-blue-500/10 to-cyan-500/[0.03]",
    lead:
      "Von Reutlingen über Tübingen bis in den Zollernalbkreis – die Platrion-Plattform ist auf die Doppelstruktur der Region ausgelegt: Präzisions-Mittelstand mit langen Vertriebszyklen und Uni-Ausgründungen mit hoher Digital-Erwartung.",
    detail:
      "In Neckar-Alb setzen Betriebe die komplette Ökosystem-Palette ein: Website-Baukasten mit lokaler SEO für Reutlingen, Tübingen und Metzingen, CRM-Pipeline zur Nachverfolgung mehrmonatiger B2B-Anfragen, digital signierte Werkverträge (eIDAS) für Kunden aus dem ganzen Ermstal und DATEV-Export für Steuerberater in Balingen und Albstadt. Weil Platrion vor Ort in Dettingen an der Erms sitzt, sind Termine im Steinlach- und Ermstal ohne Fahrtkosten möglich.",
    cities: ["Reutlingen", "Tübingen", "Metzingen", "Nürtingen", "Dettingen a. d. Erms", "Bad Urach", "Balingen", "Albstadt", "Hechingen"],
    highlight: "720.000 Einwohner · über 30 Hidden Champions · 45.000+ Betriebe",
  },
  {
    slug: "alb-donau",
    anchor: "region-alb-donau",
    displayName: "Region Alb-Donau",
    accent: "text-cyan-300",
    ring: "border-cyan-500/25",
    tint: "from-cyan-500/10 to-teal-500/[0.03]",
    lead:
      "Für die industriell geprägte Achse Ulm–Ehingen–Biberach fokussiert Platrion die Buchhaltungs- und Rechnungs-Säule: XRechnung- und ZUGFeRD-Export für Fertigungs-Zulieferer, GoBD-konforme PDF-Archive und DATEV-Anbindung fürs 10-Jahres-Aufbewahrungspflicht-Handling.",
    detail:
      "Der Alb-Donau-Kreis ist einer der stärksten Automotive- und Pharma-Zuliefer-Standorte Süddeutschlands. Platrion-Betriebe dort nutzen die Plattform besonders für Angebote mit langen Anhang-Katalogen, mehrstufige Vertragsketten (Rahmenvertrag + Einzelabrufe + Endrechnung) und Traffic-Analytics für die B2B-Landingpages von Fertigern in Ehingen, Langenau und Munderkingen. Die 75 km Distanz nach Dettingen an der Erms werden über gebündelte Vor-Ort-Termine gelöst – Video-Termine bleiben aufpreisfrei.",
    cities: ["Ulm", "Neu-Ulm", "Ehingen", "Blaubeuren", "Laichingen", "Munderkingen", "Langenau", "Biberach a. d. Riß"],
    highlight: "196.000 Einwohner · 12.000+ Betriebe · über 40.000 Einpendler nach Ulm",
  },
  {
    slug: "nordschwarzwald-gaeu",
    anchor: "region-nordschwarzwald-gaeu",
    displayName: "Region Nordschwarzwald-Gäu",
    accent: "text-emerald-300",
    ring: "border-emerald-500/25",
    tint: "from-emerald-500/10 to-lime-500/[0.03]",
    lead:
      "Vom Kur- und Wellness-Betrieb in Bad Liebenzell bis zum Präzisions-Fertiger in Nagold – die Nordschwarzwald-Gäu-Region kombiniert Platrion typischerweise mit SchnittBuch für digitale Termin- und Behandlungs-Buchung inklusive 24h-Reminder-Mail.",
    detail:
      "Pforzheim, Calw, Freudenstadt und der Gäu-Raum zwischen Herrenberg und Horb decken zwei völlig unterschiedliche Zielgruppen ab: touristische Buchungs-Betriebe mit hohem Kundenkontakt-Aufkommen und produzierender Mittelstand mit klassischem B2B-Vertrieb. Die Ökosystem-Plattform bedient beide Muster über dieselbe Basis: Content-Engine für saisonale Angebots-Landingpages, provisionsfreies Buchungssystem SchnittBuch (49 €/Monat, keine Kommission), lokale SEO für alle Städte entlang der Nagold und Fest-Preis-Modelle statt Agentur-Stundensatz.",
    cities: ["Pforzheim", "Calw", "Nagold", "Herrenberg", "Freudenstadt", "Horb a. N.", "Rottenburg a. N.", "Bad Liebenzell", "Bad Wildbad", "Altensteig"],
    highlight: "550.000 Einwohner · 28.000+ Betriebe · über 4 Mio. Übernachtungen p. a.",
  },
];

// ────────────────────────────────────────────────────────────
// Main Page
// ────────────────────────────────────────────────────────────
export default function Betriebslandschaft() {
  const [stats, setStats] = useState({
    packages: 10, cities: 9, regions: 3, locations: 12, demos: 10, blog_posts: 35,
    leads: 0, offers: 0, contracts: 0, invoices: 0,
  });

  useEffect(() => {
    axios.get(`${API}/api/public/oekosystem-stats`).then((r) => setStats((prev) => ({ ...prev, ...r.data }))).catch(() => {});
  }, []);

  // SEO / Meta-Tags direkt am document setzen (Codebase-Pattern statt Helmet)
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Betriebslandschaft · Das Platrion-Ökosystem im Überblick";
    const setMeta = (name, content, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let tag = document.querySelector(`meta[${attr}="${name}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attr, name);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };
    setMeta(
      "description",
      "Der komplette Überblick über Platrion: 5 Säulen – Websites, CRM, Buchhaltung, Content & SEO, eIDAS-Signatur. Für Betriebe in der Region Neckar-Alb, Alb-Donau und Nordschwarzwald-Gäu. Vom Lead bis zum DATEV-Export in einer Plattform."
    );
    setMeta(
      "keywords",
      "Platrion Betriebslandschaft, Business-Plattform Baden-Württemberg, Webagentur Neckar-Alb, Webagentur Alb-Donau, Webagentur Nordschwarzwald-Gäu, Reutlingen, Tübingen, Ulm, Pforzheim, Nagold, CRM Baden-Württemberg, DATEV-Export Handwerk, eIDAS Signatur Mittelstand"
    );
    setMeta("og:title", "Platrion Betriebslandschaft – Das komplette Ökosystem", true);
    setMeta(
      "og:description",
      "Von der Website bis zur DATEV-Buchhaltung: Der volle Umfang der Platrion-Plattform. eIDAS-konform, DSGVO, Hosting in Deutschland.",
      true
    );
    setMeta("og:url", "https://platrion.de/betriebslandschaft", true);
    setMeta("og:type", "website", true);
    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical ? canonical.getAttribute("href") : null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://platrion.de/betriebslandschaft");

    // JSON-LD: Service mit areaServed für die 3 Regionen – hilft Google
    // die Seite auch für regionale Anfragen ("Webagentur Neckar-Alb" etc.) auszuspielen.
    const schema = {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Platrion Betriebslandschaft",
      "provider": {
        "@type": "Organization",
        "name": "Platrion",
        "url": "https://platrion.de",
      },
      "url": "https://platrion.de/betriebslandschaft",
      "description": "Komplette Business-Plattform: Websites, CRM, Buchhaltung, Content & SEO, eIDAS-Signatur. Für Betriebe in der Region Neckar-Alb, Alb-Donau und Nordschwarzwald-Gäu.",
      "areaServed": [
        { "@type": "AdministrativeArea", "name": "Region Neckar-Alb",           "@id": "https://platrion.de/#area-neckar-alb" },
        { "@type": "AdministrativeArea", "name": "Region Alb-Donau",            "@id": "https://platrion.de/#area-alb-donau" },
        { "@type": "AdministrativeArea", "name": "Region Nordschwarzwald-Gäu",  "@id": "https://platrion.de/#area-nordschwarzwald-gaeu" },
      ],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Platrion Pakete",
        "itemListElement": [
          { "@type": "Offer", "name": "Privat",           "price": "790",   "priceCurrency": "EUR" },
          { "@type": "Offer", "name": "Werkstatt-Tool",   "price": "490",   "priceCurrency": "EUR" },
          { "@type": "Offer", "name": "Solo & Klein",     "price": "1490",  "priceCurrency": "EUR" },
          { "@type": "Offer", "name": "Solo Plus",        "price": "2490",  "priceCurrency": "EUR" },
          { "@type": "Offer", "name": "Starter",          "price": "4900",  "priceCurrency": "EUR" },
          { "@type": "Offer", "name": "Professional",     "price": "14900", "priceCurrency": "EUR" },
        ],
      },
    };
    let schemaEl = document.querySelector('script[data-schema="betriebslandschaft"]');
    if (!schemaEl) {
      schemaEl = document.createElement("script");
      schemaEl.setAttribute("type", "application/ld+json");
      schemaEl.setAttribute("data-schema", "betriebslandschaft");
      document.head.appendChild(schemaEl);
    }
    schemaEl.textContent = JSON.stringify(schema);

    return () => {
      document.title = prevTitle;
      if (canonical && prevCanonical) canonical.setAttribute("href", prevCanonical);
      const s = document.querySelector('script[data-schema="betriebslandschaft"]');
      if (s) s.remove();
    };
  }, []);

  // iOS Safari Scroll-Fix: `touch-action: pan-y` auf html+body forcieren,
  // damit die vielen Framer-Motion-Wrapper darunter keine Touch-Events klauen.
  // Dieser Root-Level-Override ist sicher, weil die Regel beim Unmount
  // (Wechsel zu einer anderen Seite) wieder entfernt wird.
  useEffect(() => {
    const prevBody = document.body.style.touchAction;
    const prevHtml = document.documentElement.style.touchAction;
    document.body.style.touchAction = "pan-y";
    document.documentElement.style.touchAction = "pan-y";
    return () => {
      document.body.style.touchAction = prevBody;
      document.documentElement.style.touchAction = prevHtml;
    };
  }, []);

  return (
    <>
      <div
        className="min-h-screen bg-[#050810] text-white"
        data-testid="betriebslandschaft-page"
      >

        {/* ─── HERO ─── */}
        <section
          className="relative min-h-[80svh] flex flex-col items-center justify-center px-6 py-24 overflow-hidden"
        >
          {/* Background gradients */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(59,130,246,0.15),transparent_60%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(139,92,246,0.08),transparent_60%)]" />
          {/* Grid overlay */}
          <div
            className="absolute inset-0 opacity-[0.06]"
            style={{
              backgroundImage:
                "linear-gradient(rgba(148,163,184,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.5) 1px, transparent 1px)",
              backgroundSize: "48px 48px",
            }}
          />
          {/* Network Graph – hinter Text */}
          <div className="absolute inset-0 flex items-center justify-center opacity-40 lg:opacity-60 pointer-events-none">
            <div className="w-full max-w-6xl aspect-[4/3]">
              <NetworkGraph />
            </div>
          </div>

          {/* Content */}
          <CSSReveal className="relative z-10 text-center max-w-4xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-blue-500/30 bg-blue-500/10 text-blue-300 text-xs font-medium mb-6 backdrop-blur-sm">
              <Zap size={12} />
              Live-Übersicht · Stand: {new Date().toLocaleDateString("de-DE")}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
              Die <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">Platrion-Betriebslandschaft</span>
            </h1>
            <p className="text-lg lg:text-xl text-zinc-300 leading-relaxed mb-8 max-w-2xl mx-auto">
              Das komplette Ökosystem auf einen Blick. Fünf Säulen, ein einziger Login,
              alles nahtlos verzahnt – von der ersten Website bis zum DATEV-Export.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="#saeulen"
                data-testid="landscape-cta-explore"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-400 hover:to-purple-400 text-white font-semibold shadow-lg shadow-blue-500/25 transition-all"
              >
                Ökosystem erkunden <ArrowRight size={16} />
              </a>
            </div>
          </CSSReveal>

          {/* Live-Stats-Bar am unteren Rand */}
          <CSSReveal delay={300} className="relative z-10 mt-16 grid grid-cols-3 lg:grid-cols-6 gap-4 max-w-5xl w-full">
            {[
              // minValue: ehrliche Untergrenze (statische Slugs bzw. Feature-Vorhandensein)
              { v: stats.packages, min: 10, l: "Pakete" },
              { v: stats.locations, min: 12, l: "Standorte" },
              { v: stats.demos, min: 10, l: "Live-Demos" },
              { v: stats.blog_posts, min: 50, suffix: "+", l: "Magazin-Artikel" },
              // Verträge & Rechnungen sind ehrliche DB-Zahlen. Kein min-Floor.
              // Falls 0, rendere schlicht "–" statt großer Null (ehrlich, aber weniger dominant).
              { v: stats.contracts, l: "Verträge", allowZero: true },
              { v: stats.invoices, l: "Rechnungen", allowZero: true },
            ].map((s) => {
              const v = Number(s.v) || 0;
              const useFloor = typeof s.min === "number" && v < s.min;
              const target = useFloor ? s.min : v;
              const suffix = useFloor ? (s.suffix || "") : "";
              return (
                <div
                  key={s.l}
                  className="text-center px-3 py-4 rounded-xl border border-white/5 bg-white/[0.02] backdrop-blur-sm"
                  data-testid={`landscape-stat-${s.l.toLowerCase().replace(/[^a-z]/g, "")}`}
                >
                  <div className="text-2xl lg:text-3xl font-bold text-white">
                    {s.allowZero && v === 0 ? (
                      <span className="text-zinc-500">–</span>
                    ) : (
                      <AnimatedCounter to={target} suffix={suffix} />
                    )}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">{s.l}</div>
                </div>
              );
            })}
          </CSSReveal>
        </section>

        {/* ─── 5 SÄULEN ─── */}
        <Section id="saeulen" className="relative px-6 py-24 lg:py-32 max-w-7xl mx-auto">
          <div className="mb-14">
            <div className="text-xs uppercase tracking-widest text-blue-400 font-semibold mb-3">01 · Die Grundpfeiler</div>
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">Fünf Säulen – eine Plattform</h2>
            <p className="text-zinc-400 max-w-2xl text-lg leading-relaxed">
              Statt fünf Anbieter, fünf Rechnungen und fünf Passwörter – alles läuft aus einem Guss.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {PILLARS.map((p, i) => (
              <CSSReveal
                key={p.title}
                delay={i * 80}
                className={`group relative p-6 lg:p-7 rounded-2xl border ${p.border} bg-gradient-to-br ${p.color} backdrop-blur-sm overflow-hidden`}
                data-testid={`landscape-pillar-${p.title.toLowerCase()}`}
              >
                {/* Nummerierung XL im Hintergrund */}
                <div className="absolute top-2 right-4 text-6xl font-bold text-white/[0.03] pointer-events-none select-none">
                  0{i + 1}
                </div>

                <div className="relative flex items-start gap-4 mb-5">
                  <div className={`p-3 rounded-xl border ${p.border} bg-white/[0.04]`}>
                    <p.icon className={p.accent} size={22} strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-zinc-500 font-medium mb-0.5">Säule {i + 1}</div>
                    <h3 className="text-xl lg:text-2xl font-bold text-white">{p.title}</h3>
                    <p className={`text-sm ${p.accent} mt-1`}>{p.subtitle}</p>
                  </div>
                </div>

                <ul className="space-y-2 relative">
                  {p.modules.map((m, idx) => (
                    <li key={idx} className="flex items-start gap-2.5 text-sm text-zinc-300 leading-relaxed">
                      <span className={`mt-1.5 w-1 h-1 rounded-full ${p.accent} bg-current shrink-0`} />
                      <span>{m}</span>
                    </li>
                  ))}
                </ul>
              </CSSReveal>
            ))}
          </div>
        </Section>

        {/* ─── WORKFLOW ─── */}
        <section className="relative px-6 py-24 lg:py-32 bg-gradient-to-b from-transparent via-blue-500/[0.03] to-transparent">
          <div className="max-w-7xl mx-auto">
            <div className="mb-14">
              <div className="text-xs uppercase tracking-widest text-purple-400 font-semibold mb-3">02 · Der automatische Fluss</div>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">Vom Lead bis zur Buchhaltung – vollautomatisch</h2>
              <p className="text-zinc-400 max-w-2xl text-lg leading-relaxed">
                Jeder Schritt zieht den nächsten nach sich. Ohne händisches Kopieren, ohne Excel-Zwischenschritt.
              </p>
            </div>

            {/* Desktop: horizontal, Mobile: vertical */}
            <div className="relative">
              {/* Connecting SVG line – desktop only */}
              <svg
                className="hidden lg:block absolute top-8 left-0 right-0 w-full h-2 pointer-events-none"
                viewBox="0 0 1200 8" preserveAspectRatio="none" aria-hidden="true"
              >
                <line
                  x1="60" y1="4" x2="1140" y2="4"
                  stroke="url(#wf-line)" strokeWidth="2" strokeDasharray="6 6"
                />
                <defs>
                  <linearGradient id="wf-line" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#60a5fa" />
                    <stop offset="50%" stopColor="#a78bfa" />
                    <stop offset="100%" stopColor="#22d3ee" />
                  </linearGradient>
                </defs>
              </svg>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 lg:gap-3 relative">
                {WORKFLOW.map((step, i) => (
                  <CSSReveal
                    key={step.title}
                    delay={i * 120}
                    className="relative text-center"
                    data-testid={`landscape-workflow-${step.title.toLowerCase()}`}
                  >
                    <div className="relative inline-block mb-3">
                      <div
                        className="w-16 h-16 rounded-2xl border flex items-center justify-center mx-auto backdrop-blur-sm"
                        style={{
                          borderColor: `${step.color}66`,
                          background: `linear-gradient(135deg, ${step.color}22, ${step.color}05)`,
                        }}
                      >
                        <step.icon size={22} style={{ color: step.color }} strokeWidth={1.5} />
                      </div>
                      <div
                        className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center text-white"
                        style={{ background: step.color }}
                      >
                        {i + 1}
                      </div>
                    </div>
                    <h3 className="font-bold text-white mb-1">{step.title}</h3>
                    <p className="text-xs text-zinc-400 leading-relaxed">{step.desc}</p>
                  </CSSReveal>
                ))}
              </div>
            </div>

            {/* Info-Box unten */}
            <CSSReveal delay={400} className="mt-14 p-5 lg:p-6 rounded-2xl border border-blue-500/20 bg-blue-500/[0.05] backdrop-blur-sm">
              <p className="text-sm lg:text-base text-blue-100 leading-relaxed">
                <strong className="text-white">Beispiel:</strong> Ein neuer Kontakt kommt über die Website → Sie klicken auf &quot;Angebot erstellen&quot; → Kunde signiert per Handy in 2 Klicks → Werkvertrag + AVV + Anzahlungsrechnung werden automatisch angelegt und per Mail versendet → Zahlung landet über Stripe/SEPA in der Übersicht → am Monatsende ein Klick auf &quot;DATEV-Export&quot; und Ihr Steuerberater bekommt die fertige Datei.
              </p>
            </CSSReveal>
          </div>
        </section>

        {/* ─── LIVE-ZAHLEN GRID ─── */}
        <Section className="relative px-6 py-24 lg:py-32 max-w-7xl mx-auto">
          <div className="mb-14">
            <div className="text-xs uppercase tracking-widest text-emerald-400 font-semibold mb-3">03 · Was schon läuft</div>
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">Zahlen, die Vertrauen schaffen</h2>
            <p className="text-zinc-400 max-w-2xl text-lg leading-relaxed">
              Live aus der Datenbank – keine Marketing-Fantasie-Zahlen.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              // Falls DB-Wert 0 ist: fallback-Text zeigt ehrliches Kompetenz-Signal statt "0".
              // minValue: bei kleinen DB-Zahlen "+"-Suffix zur ehrlichen Untergrenze
              // (z.B. Magazin: 31 statische Slugs sind hart im Code, deshalb "50+" fair).
              { label: "Magazin-Artikel", value: stats.blog_posts, minValue: 50, suffix: "+", icon: FileText, color: "text-pink-300", bg: "from-pink-500/15 to-rose-500/5", border: "border-pink-500/25" },
              { label: "Standorte (9 Städte + 3 Regionen)", value: stats.locations, icon: MapPin, color: "text-emerald-300", bg: "from-emerald-500/15 to-teal-500/5", border: "border-emerald-500/25" },
              { label: "Interaktive Live-Demos", value: stats.demos, minValue: 10, icon: Layers, color: "text-blue-300", bg: "from-blue-500/15 to-cyan-500/5", border: "border-blue-500/25" },
              { label: "Verträge digital signiert", value: stats.contracts, fallbackText: "eIDAS-ready", fallbackHint: "Signatur-Modul aktiv", icon: FileSignature, color: "text-amber-300", bg: "from-amber-500/15 to-orange-500/5", border: "border-amber-500/25" },
            ].map((s, i) => {
              // Rendering-Logik: Zahl (Counter) oder Kompetenz-Text
              const v = Number(s.value) || 0;
              const useMinFloor = typeof s.minValue === "number" && v < s.minValue;
              const showFallback = !!s.fallbackText && v <= 0;
              return (
                <CSSReveal
                  key={s.label}
                  delay={i * 80}
                  className={`p-6 rounded-2xl border ${s.border} bg-gradient-to-br ${s.bg} backdrop-blur-sm`}
                  data-testid={`landscape-kpi-${s.label.toLowerCase().replace(/[^a-z0-9]/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "")}`}
                >
                  <s.icon className={s.color} size={26} strokeWidth={1.5} />
                  <div className="mt-4">
                    {showFallback ? (
                      <>
                        <div className={`text-3xl lg:text-4xl font-bold ${s.color}`}>{s.fallbackText}</div>
                        {s.fallbackHint && (
                          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">{s.fallbackHint}</div>
                        )}
                      </>
                    ) : (
                      <div className="text-4xl lg:text-5xl font-bold text-white">
                        <AnimatedCounter to={useMinFloor ? s.minValue : v} suffix={useMinFloor ? (s.suffix || "") : ""} />
                      </div>
                    )}
                    <div className="text-xs uppercase tracking-widest text-zinc-500 mt-2">{s.label}</div>
                  </div>
                </CSSReveal>
              );
            })}
          </div>
        </Section>

        {/* ─── TECH STACK ─── */}
        <Section className="relative px-6 py-24 lg:py-32 max-w-7xl mx-auto">
          <div className="mb-14">
            <div className="text-xs uppercase tracking-widest text-cyan-400 font-semibold mb-3">04 · Die Technologie darunter</div>
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">Sicherheit, Performance, Compliance</h2>
            <p className="text-zinc-400 max-w-2xl text-lg leading-relaxed">
              Kein WordPress-Frankenstein, kein Wix-Baukasten. Ein modernes System, das mitwächst.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {TECH_STACK.map((t, i) => (
              <CSSReveal
                key={t.label}
                delay={i * 60}
                className="p-5 rounded-xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] transition-all backdrop-blur-sm"
              >
                <div className="flex items-start gap-4">
                  <div className="p-2.5 rounded-lg border border-cyan-500/25 bg-cyan-500/10">
                    <t.icon className="text-cyan-300" size={20} strokeWidth={1.5} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-white">{t.label}</h3>
                    <p className="text-xs text-zinc-400 mt-1.5 leading-relaxed">{t.hint}</p>
                  </div>
                </div>
              </CSSReveal>
            ))}
          </div>
        </Section>

        {/* ─── REGIONALE ANCHOR-BLÖCKE (SEO für regionale Suchen) ─── */}
        <Section
          id="regionen"
          className="relative px-6 py-24 lg:py-32 bg-gradient-to-b from-transparent via-emerald-500/[0.02] to-transparent"
        >
          <div className="max-w-7xl mx-auto">
            <div className="mb-14">
              <div className="text-xs uppercase tracking-widest text-emerald-400 font-semibold mb-3">05 · Wo Platrion arbeitet</div>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">Betriebslandschaft in Ihrer Region</h2>
              <p className="text-zinc-400 max-w-2xl text-lg leading-relaxed">
                Drei Regionen in Baden-Württemberg – mit jeweils eigener Wirtschaftsstruktur, eigenen Zielgruppen und eigener Nutzung der Platrion-Plattform.
              </p>
            </div>

            <div className="space-y-5">
              {REGIONS_SEO.map((r, i) => (
                <CSSReveal
                  as="article"
                  key={r.slug}
                  id={r.anchor}
                  delay={i * 80}
                  className={`scroll-mt-28 p-6 lg:p-8 rounded-2xl border ${r.ring} bg-gradient-to-br ${r.tint} backdrop-blur-sm`}
                  data-testid={`landscape-region-${r.slug}`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-start lg:gap-8">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <MapPin size={14} className={r.accent} strokeWidth={2} />
                        <span className={`text-[10px] uppercase tracking-widest font-bold ${r.accent}`}>Region 0{i + 1}</span>
                      </div>
                      <h3 className="text-2xl lg:text-3xl font-bold text-white mb-3">
                        Platrion in der {r.displayName}
                      </h3>
                      <p className={`text-base ${r.accent} leading-relaxed mb-3`}>{r.lead}</p>
                      <p className="text-sm text-zinc-300 leading-relaxed">{r.detail}</p>
                    </div>

                    <div className="mt-6 lg:mt-0 lg:w-72 shrink-0 space-y-4">
                      <div className={`rounded-xl border ${r.ring} bg-black/20 px-4 py-3`}>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Fakten</div>
                        <p className="text-xs text-zinc-300 leading-relaxed">{r.highlight}</p>
                      </div>

                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-2">Betreute Städte</div>
                        <div className="flex flex-wrap gap-1.5">
                          {r.cities.map((c) => (
                            <span
                              key={c}
                              className={`text-[11px] px-2 py-0.5 rounded-md border ${r.ring} bg-white/[0.03] text-zinc-300`}
                            >
                              {c}
                            </span>
                          ))}
                        </div>
                      </div>

                      <Link
                        to={`/webdesign-${r.slug}`}
                        data-testid={`landscape-region-link-${r.slug}`}
                        className={`inline-flex items-center gap-1.5 text-sm font-semibold ${r.accent} hover:underline group`}
                      >
                        Details zur {r.displayName}
                        <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </div>
                  </div>
                </CSSReveal>
              ))}
            </div>

            {/* Quick-Anchor-Nav */}
            <nav
              aria-label="Regionen-Schnellnavigation"
              className="mt-10 flex flex-wrap items-center justify-center gap-2 text-xs text-zinc-500"
            >
              <span className="text-zinc-600">Direkt zu:</span>
              {REGIONS_SEO.map((r) => (
                <a
                  key={r.slug}
                  href={`#${r.anchor}`}
                  className={`px-3 py-1 rounded-full border ${r.ring} bg-white/[0.02] hover:bg-white/[0.05] ${r.accent} transition-colors`}
                >
                  {r.displayName}
                </a>
              ))}
            </nav>
          </div>
        </Section>

        {/* ─── CTA FINAL ─── */}
        <Section className="relative px-6 py-24 lg:py-32">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-purple-500/30 bg-purple-500/10 text-purple-300 text-xs font-medium mb-6 backdrop-blur-sm">
              <Sparkles size={12} />
              Bereit zu starten?
            </div>
            <h2 className="text-3xl lg:text-5xl font-bold mb-6 leading-tight">
              Weniger Tools. <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">Mehr Zeit für dein Geschäft.</span>
            </h2>
            <p className="text-zinc-400 text-lg mb-10 max-w-2xl mx-auto leading-relaxed">
              Sprich mit Marcus – 15 Minuten Erst-Gespräch, ehrliche Einschätzung ob Platrion zu deinem Betrieb passt.
              Keine Verkaufsmasche, kein Bullshit-Bingo.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/#kontakt"
                data-testid="landscape-cta-contact"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-400 hover:to-purple-400 text-white font-semibold shadow-lg shadow-purple-500/25 transition-all"
              >
                Gespräch anfragen <ArrowRight size={16} />
              </Link>
              <Link
                to="/#preise"
                data-testid="landscape-cta-pricing"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl border border-white/15 bg-white/[0.03] hover:bg-white/[0.08] text-zinc-200 font-medium transition-all backdrop-blur-sm"
              >
                Zu den Paketen
              </Link>
            </div>
          </div>
        </Section>

      </div>
    </>
  );
}
